/**
 * Pantalla de desactivación de cuenta
 * Migrada desde la app móvil con TanStack Query y arquitectura moderna
 */

import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { FiShield, FiEyeOff, FiTrash, FiArrowLeft } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAuthQuery } from '../../hooks/useAuthQuery';
import { useDeactivateAccountMutation } from '../../hooks/useAuthMutations';
import PasswordVerificationScreen from './PasswordVerificationScreen';

/**
 * Componente principal del formulario de desactivación de cuenta
 */
const CloseAccountForm = () => {
  const { user } = useAuth();
  const { sessionQuery } = useAuthQuery();
  const navigate = useNavigate();
  
  // Estados locales para el countdown
  const initialSeconds = 60;
  const [seconds, setSeconds] = useState(initialSeconds);
  const [running, setRunning] = useState(true);

  // Hooks de TanStack Query
  const deactivateAccountMutation = useDeactivateAccountMutation({
    onSuccess: (data) => {
      // Mostrar mensaje de éxito
      toast.success(data?.data?.message || 'Cuenta desactivada correctamente');

      // Cerrar sesión y redirigir
      setTimeout(async () => {
        try {
          // Redirigir a login
          navigate('/login');
        } catch (error) {
          console.error('[CloseAccount] Error en logout:', error);
          toast.error('Error cerrando sesión');
        }
      }, 1000);
    },
    onError: (error) => {
      // Mostrar mensaje de error
      toast.error("Error desactivando cuenta");
    }
  });

  // Efecto para el countdown
  useEffect(() => {
    let countdown;

    if (running) {
      countdown = setInterval(() => {
        setSeconds((prevSeconds) => prevSeconds - 1);
      }, 1000);
    }

    return () => clearInterval(countdown);
  }, [running]);

  // Efecto para detener el countdown cuando llega a 0
  useEffect(() => {
    if (seconds === 0) {
      setRunning(false);
    }
  }, [seconds]);

  /**
   * Maneja el proceso de desactivación de cuenta
   */
  const handleDeactivateAccount = async () => {
    const session = sessionQuery.data?.session;
    
    if (!user?.id || !session) {
      toast.error("No hay sesión activa");
      return;
    }

    // Ejecutar desactivación
    deactivateAccountMutation.mutate(session);
  };

  return (
    <div className="min-h-screen bg-gray-900 p-4">
      <div className="max-w-2xl mx-auto">
        {/* Header con botón de retroceso */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-400 hover:text-white transition-colors mr-4"
            disabled={deactivateAccountMutation.isLoading}
          >
            <FiArrowLeft className="h-6 w-6" />
          </button>
          <h1 className="text-2xl font-bold text-white">
            Desactivar mi cuenta
          </h1>
        </div>

        {/* Card con información de desactivación */}
        <div className="bg-gray-800 rounded-lg shadow-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-white">
              Desactivar mi cuenta
            </h3>
            <FiShield 
              className="h-6 w-6 text-gray-400" 
            />
          </div>
          
          {/* Lista de consecuencias de desactivación */}
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 bg-gray-700 rounded-lg">
              <FiEyeOff 
                className="h-6 w-6 text-gray-400 mt-1 flex-shrink-0"
              />
              <p className="text-base text-gray-300">
                Dejarás de ser visible en el Radar.
              </p>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-gray-700 rounded-lg">
              <p className="text-base text-gray-300">
                Dejarás de recibir notificaciones.
              </p>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-gray-700 rounded-lg">
              <FiTrash 
                className="h-6 w-6 text-gray-400 mt-1 flex-shrink-0"
              />
              <p className="text-base text-gray-300">
                Pasados 90 días tu cuenta se eliminará.
              </p>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-gray-700 rounded-lg">
              <p className="text-base text-gray-300">
                Vuelve a iniciar sesión para revertirlo.
              </p>
            </div>
          </div>
        </div>

        {/* Botón de desactivación con countdown */}
        <div className="bg-gray-800 rounded-lg shadow-xl p-6">
          <button
            onClick={handleDeactivateAccount}
            disabled={seconds > 0 || deactivateAccountMutation.isLoading}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center
              ${seconds > 0 || deactivateAccountMutation.isLoading
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 text-white'
              }`}
          >
            {deactivateAccountMutation.isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Procesando...
              </>
            ) : seconds > 0 ? (
              `${seconds} Seg`
            ) : (
              'Desactivar mi cuenta'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Componente principal de la pantalla de cierre de cuenta
 */
const CloseAccountScreen = () => {
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  const { sessionQuery } = useAuthQuery();
  const isLoading = sessionQuery.isLoading;

  /**
   * Maneja la verificación exitosa de la contraseña
   */
  const handlePasswordVerified = () => {
    setIsPasswordVerified(true);
  };

  // Pantalla de carga mientras se obtiene el usuario
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Cargando...</div>
      </div>
    );
  }

  // Si no se ha verificado la contraseña, mostrar pantalla de verificación
  if (!isPasswordVerified) {
    return (
      <PasswordVerificationScreen
        title="Verificar tu contraseña"
        subtitle="Para continuar, vuelve a introducir tu contraseña de Amarsi."
        onSuccess={handlePasswordVerified}
        successMessage="Contraseña verificada correctamente"
        buttonText="Continuar"
      />
    );
  }

  // Renderizar formulario de desactivación
  return <CloseAccountForm />;
};

export default CloseAccountScreen;
